"use client";

import posthog from "posthog-js";
import { useEffect } from "react";

import { OUR_CUSTOMERS_REDESIGN_FLAG } from "@/lib/customers-experiment-flag";
import type { CustomersVariant } from "@/lib/customers-experiment-flag";

interface CustomersExposureProps {
  variant: CustomersVariant;
  /**
   * The ID the server evaluated the flag with, when it minted one. The
   * exposure is only valid for that identity.
   */
  bootstrapDistinctId?: string;
}

/**
 * Logs the experiment exposure for the variant the server rendered.
 *
 * It sends `$feature_flag_called` itself rather than reading the flag in the
 * browser, so the event always names the variant on screen, even on a client
 * navigation where the SDK's own flags may be stale. It only mounts on
 * /our-customers, so the exposure only fires for people who view this page.
 */
export function CustomersExposure({
  variant,
  bootstrapDistinctId,
}: CustomersExposureProps) {
  useEffect(() => {
    // Child effects run before the PostHog provider (a parent) initialises the
    // SDK, and posthog-js drops events captured before init. A timer runs after
    // the whole commit, including that init.
    const timer = window.setTimeout(() => {
      // On a client navigation the SDK was initialised earlier, so it never
      // adopted the ID the server minted for this render. The variant on screen
      // was not assigned to this identity, so logging it would put the person
      // in an arm they were not allocated to.
      if (
        bootstrapDistinctId !== undefined &&
        posthog.get_distinct_id() !== bootstrapDistinctId
      ) {
        return;
      }
      posthog.capture("$feature_flag_called", {
        $feature_flag: OUR_CUSTOMERS_REDESIGN_FLAG,
        $feature_flag_response: variant,
        [`$feature/${OUR_CUSTOMERS_REDESIGN_FLAG}`]: variant,
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [variant, bootstrapDistinctId]);

  return null;
}
